import StService from './StService';
import { createApi } from '@stilingue/st-auth-utils';
import gateway from './Gateway';
import serviceVersion from './service-version';
import { injectSessionIdHeader } from './session-headers';

const afterParser = response => response.data;

class UpmsLegacyService extends StService {
  constructor(gateway, version = 'staging') {
    const api = createApi(`upms-legacy/${version}/`, gateway, { timeout: 30000 });
    const companyByExamplesApi = createApi(`upms-legacy/${version}/company/by-examples`, gateway, { timeout: 120000 });
    const userByExamplesApi = createApi(`upms-legacy/${version}/user/by-examples`, gateway, { timeout: 30000 });
    const userGroupAccountsAndPermissionsApi = createApi(
      `upms-legacy/${version}/user/usergroup/accounts-and-permissions`,
      gateway,
      { timeout: 300000 },
    );
    super(api, { afterParser });
    this.companyByExamplesApi = injectSessionIdHeader(companyByExamplesApi);
    this.userByExamplesApi = injectSessionIdHeader(userByExamplesApi);
    this.userGroupAccountsAndPermissionsApi = injectSessionIdHeader(userGroupAccountsAndPermissionsApi);
  }

  getUser(userId) {
    return this.api.get({ url: `user/${userId}/internal` }).then(this.afterParser);
  }

  getUserByEmail(email) {
    return this.api.get({ url: 'user/by-email/internal', params: { email } }).then(this.afterParser);
  }

  getUserCompanies(userId) {
    return this.api.get({ url: `user/${userId}/companies` }).then(this.afterParser);
  }

  getUsersByExamples(examples) {
    return this.userByExamplesApi.post(examples).then(this.afterParser);
  }

  getUserGroupAccountsAndPermissions(usergroup) {
    return this.userGroupAccountsAndPermissionsApi.get({ params: { usergroup } }).then(this.afterParser);
  }

  getAccountUserGroup(accountId) {
    return this.api.get({ url: `account/${encodeURIComponent(accountId)}/usergroup` }).then(this.afterParser);
  }

  getUserCompanyMasterStatus(companyId, userId) {
    const params = { companyId, userId };
    return this.api.get({ url: 'user/is-company-master', params }).then(this.afterParser);
  }

  getAccountsByUsergroup(usergroup) {
    return this.api.get({ url: `account/all-by-usergroup/${encodeURIComponent(usergroup)}` }).then(this.afterParser);
  }

  removeUserFromAccountRemovedUsers(accountId, userId) {
    const url = `account/${encodeURIComponent(accountId)}/removed-users/${encodeURIComponent(userId)}`;
    return this.api.delete({ url }).then(this.afterParser);
  }

  getAccountUserRole(accountId, userId) {
    return this.api.get({ url: `role/account/${accountId}/user/${userId}` }).then(this.afterParser);
  }

  getRole(roleId) {
    return this.api.get({ url: `user-roles/role/${roleId}` }).then(this.afterParser);
  }

  getUserGroupsByExamples(examples, { page = null, size = null, sort = null } = {}) {
    const params = {};
    if (page !== null) params.page = page;
    if (size !== null) params.size = size;
    if (sort !== null) params.sort = sort;
    return this.companyByExamplesApi.post(examples, { params }).then(this.afterParser);
  }

  getUserGroupsWithCS(body) {
    return this.companyByExamplesApi.post(body).then(this.afterParser);
  }

  getCompanyById(companyId) {
    return this.api.get({ url: `company/${encodeURIComponent(companyId)}` }).then(this.afterParser);
  }

  updateCompany(company) {
    return this.api.put({
      url: 'company',
      ...company,
    }).then(this.afterParser);
  }
}

export default new UpmsLegacyService(gateway, serviceVersion);
